import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
	addDays,
	eachWeekendOfInterval,
	endOfToday,
	endOfTomorrow,
	endOfWeek,
	isSameDay,
	isToday,
	isTomorrow,
	parseISO,
	startOfToday,
	startOfTomorrow
} from 'date-fns';

const TopBar = ({
	setDates,
	setMonth,
	titleStatus,
	availableDates,
	modifiers
}) => {
	const defaultButtonParams = {
		today: { disabled: true, active: false },
		tomorrow: { disabled: true, active: false },
		weekend: { disabled: true, active: false }
	};

	const buttonParams = useMemo(
		() =>
			availableDates.reduce((memo, value) => {
				const params = memo;
				const curDay = parseISO(value);
				const saturdayThisWeek = endOfWeek(startOfToday());
				const sundayThisWeek = addDays(saturdayThisWeek, 1);

				if (isToday(curDay)) {
					params.today.disabled = false;

					if (
						isSameDay(curDay, modifiers.start) &&
						(isSameDay(curDay, modifiers.end) || !modifiers.end)
					) {
						params.today.active = true;
					}
				}

				if (isTomorrow(curDay)) {
					params.tomorrow.disabled = false;

					if (
						isSameDay(curDay, modifiers.start) &&
						(isSameDay(curDay, modifiers.end) || !modifiers.end)
					) {
						params.tomorrow.active = true;
					}
				}

				if (
					isSameDay(curDay, saturdayThisWeek) ||
					isSameDay(curDay, sundayThisWeek)
				) {
					params.weekend.disabled = false;

					if (
						isSameDay(saturdayThisWeek, modifiers.start) &&
						isSameDay(sundayThisWeek, modifiers.end)
					) {
						params.weekend.active = true;
					}
				}

				return params;
			}, defaultButtonParams),
		[availableDates, defaultButtonParams, modifiers]
	);

	return (
		<div className="DayPicker-TopBar">
			{titleStatus && <div className="DayPicker-TopBar-Title">Календарь</div>}
			<div className="DayPicker-TopBar-Options">
				<button
					type="button"
					className={cx('DayPicker-TopBar-Options-Button', {
						'DayPicker-TopBar-Options-Button-active': buttonParams.today.active
					})}
					onClick={() => {
						setMonth(startOfToday());
						setDates({
							startDate: startOfToday(),
							endDate: endOfToday()
						});
					}}
					disabled={buttonParams.today.disabled}
				>
					Сегодня
				</button>
				<button
					type="button"
					className={cx('DayPicker-TopBar-Options-Button', {
						'DayPicker-TopBar-Options-Button-active':
							buttonParams.tomorrow.active
					})}
					onClick={() => {
						setMonth(startOfTomorrow());
						setDates({
							startDate: startOfTomorrow(),
							endDate: endOfTomorrow()
						});
					}}
					disabled={buttonParams.tomorrow.disabled}
				>
					Завтра
				</button>
				<button
					type="button"
					className={cx('DayPicker-TopBar-Options-Button', {
						'DayPicker-TopBar-Options-Button-active':
							buttonParams.weekend.active
					})}
					onClick={() => {
						const curr = new Date(); // get current date
						const first = curr.getDate() - curr.getDay() + 1; // First day is the day of the month - the day of the week
						const last = first + 6; // last day is the first day + 6

						const firstday = new Date(curr.setDate(first));
						const lastday = new Date(curr.setDate(last));
						const result = eachWeekendOfInterval({
							start: firstday,
							end: lastday
						});
						result?.[1].setHours(23);
						result?.[1].setMinutes(59);
						setDates({
							startDate: result?.[0],
							endDate: result?.[1]
						});
					}}
					disabled={buttonParams.weekend.disabled}
				>
					Выходные
				</button>
			</div>
		</div>
	);
};

TopBar.propTypes = {
	setDates: PropTypes.func,
	setMonth: PropTypes.func,
	titleStatus: PropTypes.bool,
	availableDates: PropTypes.arrayOf(
		PropTypes.oneOfType([PropTypes.string, PropTypes.object])
	),
	modifiers: PropTypes.shape({
		start: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.bool]),
		end: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.bool])
	})
};

TopBar.defaultProps = {
	availableDates: [],
	modifiers: {
		start: false,
		end: false
	}
};

export default TopBar;
